import gql from "graphql-tag"

export const ResponsiveImageFields = gql`
  fragment ResponsiveImageFields on ResponsiveImage {
    srcSet
    webpSrcSet
    sizes
    src
    width
    height
    aspectRatio
    alt
    title
    bgColor
    base64
  }
`

export const ComponentCardSliderRecordFields = gql`
  fragment ComponentCardSliderRecordFields on ComponentCardSliderRecord {
    id
    __typename
    header
    ctaLink
    ctaButtonText
    ctaButtonColor
    ctaButtonTextColor
    cards {
      link
      title
      subtitle
      image {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
      fillContainer
    }
  }
`

export const ComponentProductSliderRecordFields = gql`
  fragment ComponentProductSliderRecordFields on ComponentProductSliderRecord {
    id
    __typename
    header
    ctaLink
    ctaButtonText
    products {
      slug
      productName
      price
      isDiscounted
      discountedPrice
      thumbnail {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
    }
  }
`

export const ComponentCodeSnippetRecordFields = gql`
  fragment ComponentCodeSnippetRecordFields on ComponentCodeSnippetRecord {
    id
    __typename
    code
  }
`

export const ComponentCtaBlockRecordFields = gql`
  fragment ComponentCtaBlockRecordFields on ComponentCtaBlockRecord {
    id
    __typename
    blocks {
      headline
      url
      buttonText
      buttonColor
      buttonTextColor
      backgroundImage {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
      mobileBackgroundImage {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
    }
  }
`

export const ComponentDownloadRecordFields = gql`
  fragment ComponentDownloadRecordFields on ComponentDownloadRecord {
    id
    __typename
    download {
      url
    }
    text
  }
`

export const ComponentFeatureListRecordFields = gql`
  fragment ComponentFeatureListRecordFields on ComponentFeatureListRecord {
    id
    __typename
    header
    features {
      label
      items
    }
  }
`

export const ComponentHeroRecordFields = gql`
  fragment ComponentHeroRecordFields on ComponentHeroRecord {
    id
    __typename
    header
    headerFontSize
    headerMobileFontSize
    subheader
    subheaderFontSize
    subheaderMobileFontSize
    subheaderFontStyle
    swapHeaderPositions
    useTextBackgroundOverlay
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
  }
`

export const ComponentHeroCarouselRecordFields = gql`
  fragment ComponentHeroCarouselRecordFields on ComponentHeroCarouselRecord {
    id
    __typename
    showSlide
    slides {
      heroText
      heroTextColor
      heroTextSize
      heroMobileTextSize
      heroTextTopPadding
      title
      subtitle
      textColor
      textSize
      mobileTextSize
      url
      buttonColor
      buttonText
      buttonTextColor
      arrowBackgroundColor
      backgroundImage {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
      mobileBackgroundImage {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
    }
    module
    ymmAttachment {
      fontColor
      bgColor
    }
    interval
  }
`

export const ComponentHeroInfoBlockRecordFields = gql`
  fragment ComponentHeroInfoBlockRecordFields on ComponentHeroInfoBlockRecord {
    id
    __typename
    internalTitle
    subjectImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    headerImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    layout
    headingText
    headingTextColor {
      hex
    }
    body
    bodyTextColor {
      hex
    }
    buttonText {
      value
    }
    buttonTextColor {
      hex
    }
    buttonBgColor {
      hex
    }
    buttonLink
    bgImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    bgColor {
      hex
    }
    hotspots {
      ... on HotspotRecord {
        id
        __typename
        xAxis
        yAxis
        label
        tileAlign
        imageAltText
        previewImage {
          url
        }
      }
    }
  }
`

export const ComponentFeaturedCardWithButtonRecordFields = gql`
  fragment ComponentFeaturedCardWithButtonRecordFields on ComponentFeaturedCardWithButtonRecord {
    id
    __typename
    title
    backgroundColor {
      hex
    }
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    ctaText
    ctaLink
    ctaTextColor {
      hex
    }
    ctaBackgroundColor {
      hex
    }
    featuredCards {
      ... on FeaturedCardListRecord {
        id
        __typename
        title
        titleColor {
          hex
        }
        subtitle
        subtitleColor {
          hex
        }
        image: displayImage {
          responsiveImage {
            ...ResponsiveImageFields
          }
          url
        }
        ctaText: buttonText
        ctaLink: buttonLink
        buttonTextColor {
          hex
        }
        buttonBackgroundColor {
          hex
        }
      }
    }
  }
`

export const ComponentFeaturedVideoRecordFields = gql`
  fragment ComponentFeaturedVideoRecordFields on ComponentFeaturedVideoRecord {
    id
    __typename
    title
    sectionButtonText
    sectionButtonLink
    sectionButtonTextColor {
      hex
    }
    sectionButtonColor {
      hex
    }
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    backgroundColor {
      hex
    }
    primaryVideoThumbnail {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    primaryVideoPreview {
      url
      video {
        streamingUrl
        mp4Low: mp4Url(res: low)
      }
    }
    primaryVideoLink
    leftVideoThumbnail {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    leftVideoPreview {
      url
      video {
        streamingUrl
        mp4Low: mp4Url(res: low)
      }
    }
    leftVideoLink
    rightVideoThumbnail {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    rightVideoPreview {
      url
      video {
        streamingUrl
        mp4Low: mp4Url(res: low)
      }
    }
    rightVideoLink
  }
`

export const ComponentFeaturedCardWithTextRecordFields = gql`
  fragment ComponentFeaturedCardWithTextRecordFields on ComponentFeaturedCardWithTextRecord {
    id
    __typename
    sectionTitle
    buttonText
    buttonTextColor {
      hex
    }
    buttonBackgroundColor {
      hex
    }
    textBlock
    textBlockColor {
      hex
    }
    featuredCardWithText {
      ... on FeaturedCardWithTextRecord {
        id
        __typename
        cardImage {
          responsiveImage {
            ...ResponsiveImageFields
          }
          url
        }
        cardTitle
        cardTitleColor {
          hex
        }
        description
        upperSubtitleText
        upperSubtitleColor {
          hex
        }
        lowerSubtitleText
        lowerSubtitleColor {
          hex
        }
        cardLink
      }
    }
  }
`

export const ComponentFeaturedSliderRecordFields = gql`
  fragment ComponentFeaturedSliderRecordFields on ComponentFeaturedSliderRecord {
    id
    __typename
    sectionTitle
    sectionTitleColor {
      hex
    }
    backgroundColor {
      hex
    }
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    featuredProducts {
      ... on FeaturedSliderRecord {
        id
        __typename
        productImage {
          responsiveImage {
            ...ResponsiveImageFields
          }
          url
        }
        productTitle
        subtext
        productTitleColor {
          hex
        }
        subtextColor {
          hex
        }
        buttonText
        buttonTextColor {
          hex
        }
        buttonColor {
          hex
        }
        buttonLink
      }
    }
  }
`

export const ComponentHeroImageSplitRecordFields = gql`
  fragment ComponentHeroImageSplitRecordFields on ComponentHeroImageSplitRecord {
    id
    __typename
    internalTitle
    leftImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    rightImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    leftColor {
      hex
    }
    rightColor {
      hex
    }
    imageLayout
    leftImageText
    leftImageTextColor {
      hex
    }
    leftImageUpperSubtitle
    leftImageSubtitleColor {
      hex
    }
    rightImageText
    rightImageTextColor {
      hex
    }
    rightImageUpperSubtitle
    rightImageSubtitleColor {
      hex
    }
    leftImageButtonText
    leftImageButtonLink
    leftImageButtonColor {
      hex
    }
    leftImageButtonTextColor {
      hex
    }
    rightImageButtonText
    rightImageButtonLink
    rightImageButtonColor {
      hex
    }
    rightImageButtonTextColor {
      hex
    }
  }
`

export const ComponentImageRecordFields = gql`
  fragment ComponentImageRecordFields on ComponentImageRecord {
    id
    __typename
    image {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
    mobileImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
    fullWidth
    height
    mobileHeight
    autoHeight
  }
`

export const ComponentImageGridRecordFields = gql`
  fragment ComponentImageGridRecordFields on ComponentImageGridRecord {
    id
    __typename
    images {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
    standardizeImageSizes
  }
`

export const ComponentInfoBlockRecordFields = gql`
  fragment ComponentInfoBlockRecordFields on ComponentInfoBlockRecord {
    id
    __typename
    blocks {
      heading
      body
      headingColor
      bodyColor
      backgroundImage {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
      useTextBackgroundOverlay
      map
      blockWidth
      cta
      ctaFont
      ctaBg
      ctaLink
    }
  }
`

export const ComponentTextBlockRecordFields = gql`
  fragment ComponentTextBlockRecordFields on ComponentTextBlockRecord {
    id
    __typename
    render
    header
    body
    centerHeader
    centerBody
  }
`

export const ComponentTextHeroRecordFields = gql`
  fragment ComponentTextHeroRecordFields on ComponentTextHeroRecord {
    id
    __typename
    text
    mobileText
    topPadding
    topPaddingMobile
    bottomPadding
    bottomPaddingMobile
  }
`

export const ComponentVerticalSpaceRecordFields = gql`
  fragment ComponentVerticalSpaceRecordFields on ComponentVerticalSpaceRecord {
    id
    __typename
    height
    mobileHeight
  }
`

export const ComponentVideoRecordFields = gql`
  fragment ComponentVideoRecordFields on ComponentVideoRecord {
    id
    __typename
    embedId
  }
`

export const ComponentYearSelectorRecordFields = gql`
  fragment ComponentYearSelectorRecordFields on ComponentYearSelectorRecord {
    id
    __typename
    car {
      make {
        name
        slug
      }
      model {
        name
        slug
      }
      trim {
        name
        slug
      }
      years
    }
  }
`
//Psantos addition
export const ComponentYmmSelectorRecordFields = gql`
  fragment ComponentYmmSelectorRecordFields on ComponentYmmSelectorRecord {
    id
    __typename
    fontColor
    bgColor
  }
`

export const ComponentPageHeaderRecordFields = gql`
  fragment ComponentPageHeaderRecordFields on ComponentPageHeaderRecord {
    id
    heroText
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
    mobileImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
    }
    textAlignment
    textColor
    textBgColor
    ymmAttachment {
      fontColor
      bgColor
    }
    __typename
  }
`
export const ComponentStripVideoBannerRecordFields = gql`
  fragment ComponentStripVideoBannerRecordFields on ComponentStripVideoBannerRecord {
    id
    __typename
    promoBanner {
      url
      alt
    }
    promoBannerMobile {
      url
      alt
    }
    optionValues {
      ... on ListVideoLinkRecord {
        logo {
          url
        }
        videoThumbnail {
          url
          responsiveImage {
            ...ResponsiveImageFields
          }
        }
        overlayRgb {
          hex
        }
        videoTitle
        videoLink {
          url
          video {
            streamingUrl
            mp4Low: mp4Url(res: low)
          }
        }
        descriptionLine1
        descriptionLine2
        pillText
        redirect
      }
    }
  }
`

export const ComponentBannerStripRecordFields = gql`
  fragment ComponentBannerStripRecordFields on ComponentBannerStripRecord {
    id
    __typename
    internalTitle
    backgroundColor {
      hex
    }
    backgroundImage {
      responsiveImage {
        ...ResponsiveImageFields
      }
      url
    }
    height
    text
    textColor {
      hex
    }
    fontWeight
    redirectLink
  }
`
